import { JSX, memo, useEffect, useRef, useState, MouseEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { MoreHoriz, DeleteOutlineOutlined } from '@mui/icons-material';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import {
    Box,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
} from '@mui/material';
import { CONVERSATIONS } from 'utils/config/constants/controllerNames';
import { logEventFirebase, onUpdateDoc } from 'utils/config/firebase';

import { undoDeletedDraftMessage } from 'helpers/messages';
import { errorLogger } from 'utils/config/firebase/logs';
import { makeShort } from 'helpers/strings';
import { ItemType } from 'components/Sidebar/types';

type SidebarItemProps = {
    item: ItemType;
    closeBar: () => void;
    showValue?: (val: string) => JSX.Element | null | string;
    searchValue?: string;
    onSaveHistory?: (title: string, subtitle: string, id: string) => void;
    onOpenROneModal: (jobOrderId?: string) => void;
};

const SidebarItem = ({
    item,
    closeBar,
    showValue,
    onSaveHistory,
    onOpenROneModal,
}: SidebarItemProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const { document_id } = useParams();
    const { t } = useTranslation();
    const ref = useRef<HTMLLIElement | null>(null);
    const open = Boolean(anchorEl);
    const title = makeShort(item.title, 30);
    const subtitle = makeShort(item.subtitle, 37);

    useEffect(() => {
        const onBodyClick = (e: PointerEvent) => {
            const target = e.target as Node;
            if (e.target != ref.current && !ref.current?.contains(target)) {
                setAnchorEl(null);
            }
        };
        document.body.addEventListener('click', onBodyClick);
        return () => document.body.removeEventListener('click', onBodyClick);
    }, []);

    const handleSave = () => {
        onUpdateDoc(CONVERSATIONS, item.document_id, {
            isSaved: !item.isSaved,
        });
    };

    const undoDeletedItem = (document_id: string) => {
        onUpdateDoc(CONVERSATIONS, document_id, {
            deleted: false,
            deleted_at: null,
            updated_at: new Date().toISOString(),
        });
    };

    const openUndoMessage = (document_id: string) => {
        undoDeletedDraftMessage(() => {
            undoDeletedItem(document_id);
        });
    };

    const onDeleteContext = (itemDocumentId: string) => {
        try {
            logEventFirebase('delete_generated_job');
            openUndoMessage(itemDocumentId);
            onUpdateDoc(CONVERSATIONS, itemDocumentId, {
                deleted: true,
                deleted_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });
            if (document_id === itemDocumentId) {
                navigate('/');
            }
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.critical(error?.message, 'onDeleteContext');
            }
            console.log(error);
        }
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const onClickItem = () => {
        closeBar();
        onSaveHistory?.(item.title, item.subtitle, item.document_id);
    };

    const showROneData = (item: ItemType) => {
        onOpenROneModal(item.jobOrderId);
    };

    return (
        <ListItem
            key={item.document_id}
            ref={ref}
            className={open || document_id === item.document_id ? 'active' : ''}
        >
            <Link to={`/job/${item.document_id}`}>
                <Box className="flex align-center" onClick={() => onClickItem()}>
                    <span>{item.day}</span>{' '}
                    <Box>
                        <Box className="title">{showValue ? showValue(title) : title}</Box>
                        <Box className="subtitle">{showValue ? showValue(subtitle) : subtitle}</Box>
                    </Box>
                </Box>
            </Link>
            <Box className="item-bookmark-btn">
                <button className={item.isSaved ? 'item-saved' : ''} onClick={handleSave}>
                    {item.isSaved ? <BookmarkIcon /> : <BookmarkBorderOutlinedIcon />}
                </button>
            </Box>
            <Box className="history-item-buttons">
                <Button
                    id="history-actions"
                    aria-controls={open ? 'history-popper' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    aria-label="History actions"
                    className="menu-trigger-btn"
                    onClick={handleClick}
                >
                    <MoreHoriz
                        sx={{
                            fontSize: 24,
                            color: '#fff',
                        }}
                    />
                </Button>
                <Popper
                    id="history-popper"
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1201 }}
                >
                    <Paper>
                        <List
                            id="history-popper-list"
                            aria-labelledby="history-actions"
                            className="menu-items"
                            sx={{ p: 0 }}
                        >
                            {item.jobOrderId && (
                                <ListItemButton
                                    component="button"
                                    onClick={() => {
                                        showROneData(item);
                                    }}
                                >
                                    <ListItemIcon>
                                        <WorkOutlineOutlinedIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('web_app.sidebar_right.r_one_data')} />
                                </ListItemButton>
                            )}
                            <ListItemButton
                                className="menu-delete-btn"
                                component="button"
                                onClick={() => {
                                    onDeleteContext(item.document_id);
                                }}
                            >
                                <ListItemIcon>
                                    <DeleteOutlineOutlined />
                                </ListItemIcon>
                                <ListItemText primary={t('web_app.sidebar_right.delete')} />
                            </ListItemButton>
                        </List>
                    </Paper>
                </Popper>
            </Box>
        </ListItem>
    );
};

const MemoizedItem = memo(SidebarItem, (prevProps, nextProps) => {
    if (JSON.stringify(prevProps.item) !== JSON.stringify(nextProps.item)) {
        return false;
    }
    if (prevProps.searchValue !== nextProps.searchValue) {
        return false;
    }
    return true;
});

export default MemoizedItem;
